import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import DashboardDealTracker from "./DashboardDealTracker";
import DashboardWorklogTracker from "./DashboardWorklogTracker";
import DashboardProjectTimeLogTracker from "./DashboardProjectTimeLogTracker";
import {
  compareCurrentSubdepartment,
  newDashboardMap,
} from "../new-dashboard-mapper";
import { DashboardGridSkeleton } from "../dashboard-skeletons/dashboard-skeletons";

const DashboardRealEstate = ({
  realEstateUsers,
  fetchData,
  isRELoading,
  currSubDepartment,
}) => {
  const [realEstateTrackerMembers, setRealEstateTrackerMembers] = useState([]);
  const [realEstateProjectLog, setRealEstateProjectLog] = useState({});

  useEffect(() => {
    if (realEstateUsers) {
      if (
        !realEstateUsers ||
        typeof realEstateUsers !== "object" ||
        Array.isArray(realEstateUsers)
      ) {
        console.error("Invalid prop: realEstateUsers should be an object.");
        return;
      }

      const realEstateTrackers = Array.isArray(realEstateUsers.RETrackersDtos)
        ? realEstateUsers.RETrackersDtos
        : [];
      const realEstateProjectLog = realEstateUsers.REProjectLogWidgetDto
        ? realEstateUsers.REProjectLogWidgetDto
        : [];
      setRealEstateTrackerMembers(realEstateTrackers);
      setRealEstateProjectLog(realEstateProjectLog);
    }
  }, [realEstateUsers]);

  return (
    <React.Fragment>
      {isRELoading ? (
        compareCurrentSubdepartment(
          [...newDashboardMap.RE.user, ...newDashboardMap.All.user],
          currSubDepartment
        ) ? (
          <>
            <DashboardGridSkeleton />
            <DashboardGridSkeleton />
          </>
        ) : (
          // Staffer see three widgets
          <>
            <DashboardGridSkeleton />
            <DashboardGridSkeleton />
            <DashboardGridSkeleton />
          </>
        )
      ) : (
        realEstateTrackerMembers &&
        realEstateTrackerMembers.length > 0 && (
          <React.Fragment>
            <DashboardDealTracker
              isAllRbc={realEstateUsers.IsAllRbc}
              isStaffer={realEstateUsers.IsStaffer}
              realEstateTrackerMembers={realEstateTrackerMembers}
            />
            <DashboardWorklogTracker
              isAllRbc={realEstateUsers.IsAllRbc}
              isStaffer={realEstateUsers.IsStaffer}
              currentUserId={realEstateUsers.CurrentUserId}
              realEstateTrackerMembers={realEstateTrackerMembers}
              fetchData={fetchData}
            />
            <DashboardProjectTimeLogTracker
              isStaffer={realEstateUsers.IsStaffer}
              realEstateProjectLog={realEstateProjectLog}
            />
          </React.Fragment>
        )
      )}
    </React.Fragment>
  );
};

const ColumnPropType = PropTypes.shape({
  Id: PropTypes.number.isRequired,
  Name: PropTypes.string.isRequired,
  Type: PropTypes.string.isRequired,
  Order: PropTypes.number.isRequired,
  LookupValues: PropTypes.string,
  LockStatus: PropTypes.number.isRequired,
});

const DataPropType = PropTypes.shape({
  Id: PropTypes.number.isRequired,
  ColumnId: PropTypes.number.isRequired,
  ColumnType: PropTypes.string,
  ColumnName: PropTypes.string,
  Value: PropTypes.string,
  LookupValues: PropTypes.string,
  ColumnLockStatus: PropTypes.number,
});

const RowPropType = PropTypes.shape({
  Id: PropTypes.number.isRequired,
  Row: PropTypes.number.isRequired,
  Data: PropTypes.arrayOf(DataPropType),
});

const TablePropType = PropTypes.shape({
  Columns: PropTypes.arrayOf(ColumnPropType),
  Rows: PropTypes.arrayOf(RowPropType),
});

const BookedVacationPropType = PropTypes.shape({
  StartDate: PropTypes.string.isRequired,
  EndDate: PropTypes.string.isRequired,
  AdditionalComments: PropTypes.string.isRequired,
  Upcoming: PropTypes.bool.isRequired,
  BookedVacationString: PropTypes.string.isRequired,
  Color: PropTypes.string.isRequired,
  IsApproved: PropTypes.bool.isRequired,
  IsAwaitingApproval: PropTypes.bool.isRequired,
});

const ProjectMemberDtoPropType = PropTypes.shape({
  UserId: PropTypes.number.isRequired,
  UserName: PropTypes.string,
});

const REHoursLogPropType = PropTypes.shape({
  UserId: PropTypes.number.isRequired,
  Name: PropTypes.string,
  HoursThisWeek: PropTypes.number,
  WorkingWeekend: PropTypes.bool,
  HoursNextWeek: PropTypes.number,
  LastUpdated: PropTypes.string,
});

const REProjectLogPropType = PropTypes.shape({
  ProjectId: PropTypes.number.isRequired,
  ProjectName: PropTypes.string.isRequired,
  TargetDate: PropTypes.string,
  MeetingDate: PropTypes.string,
  DateStaffed: PropTypes.string,
  OwnerUserId: PropTypes.number.isRequired,
  OwnerUserName: PropTypes.string,
  ProjectMembersList: PropTypes.arrayOf(ProjectMemberDtoPropType),
  Status: PropTypes.string,
  StatusColor: PropTypes.string,
  Active: PropTypes.number.isRequired,
  HoursWorkedInCurrentWeek: PropTypes.number,
  CountWorkingOnTheWeekend: PropTypes.number,
  HoursForTheNextWeek: PropTypes.number,
  MD: PropTypes.string,
  D: PropTypes.string,
  VP: PropTypes.string,
  Associate: PropTypes.string,
  Analyst: PropTypes.string,
  Notes: PropTypes.string,
  Description: PropTypes.string,
  HoursLog: PropTypes.arrayOf(REHoursLogPropType),
});

DashboardRealEstate.propTypes = {
  realEstateUsers: PropTypes.shape({
    IsAllRbc: PropTypes.bool.isRequired,
    IsStaffer: PropTypes.bool.isRequired,
    CurrentUserId: PropTypes.number.isRequired,
    RETrackersDtos: PropTypes.arrayOf(
      PropTypes.shape({
        CompanyId: PropTypes.number.isRequired,
        CompanyName: PropTypes.string.isRequired,
        UserId: PropTypes.number.isRequired,
        Name: PropTypes.string.isRequired,
        Type: PropTypes.string.isRequired,
        TypeColor: PropTypes.string.isRequired,
        ActiveDeals: PropTypes.number.isRequired,
        LastUpdated: PropTypes.string.isRequired,
        HoursThisWeek: PropTypes.number.isRequired,
        WorkingWeekend: PropTypes.bool.isRequired,
        HoursNextWeek: PropTypes.number.isRequired,
        Comments: PropTypes.string,
        DealTracker: PropTypes.arrayOf(
          PropTypes.shape({
            ProjectId: PropTypes.number.isRequired,
            ProjectName: PropTypes.string.isRequired,
            OwnerUserId: PropTypes.number,
            OwnerUserName: PropTypes.string,
            EditorUserId: PropTypes.number.isRequired,
            EditorUserName: PropTypes.string.isRequired,
            Editors: PropTypes.array,
            Type: PropTypes.string,
            TypeColor: PropTypes.string,
            ActiveDeals: PropTypes.number.isRequired,
            LastUpdated: PropTypes.string,
            AllowDealsEdit: PropTypes.bool.isRequired,
            ProjectMembersList: PropTypes.array,
            TableDeals: PropTypes.arrayOf(TablePropType),
          })
        ).isRequired,
        VacationTracker: PropTypes.arrayOf(
          PropTypes.shape({
            TaskId: PropTypes.number.isRequired,
            TaskName: PropTypes.string.isRequired,
            ProjectId: PropTypes.number.isRequired,
            ProjectName: PropTypes.string,
            OwnerUserId: PropTypes.number,
            OwnerUserName: PropTypes.string,
            EditorUserId: PropTypes.number.isRequired,
            EditorUserName: PropTypes.string.isRequired,
            Type: PropTypes.string,
            TypeColor: PropTypes.string,
            LastUpdated: PropTypes.string,
            VacationsTaskId: PropTypes.number.isRequired,
            VacationsTaskName: PropTypes.string.isRequired,
            VacationsProjectId: PropTypes.number.isRequired,
            AllowVacationsEdit: PropTypes.bool.isRequired,
            TableVacation: PropTypes.arrayOf(TablePropType),
            BookedVacations: PropTypes.arrayOf(BookedVacationPropType),
          })
        ).isRequired,
        BookedVacations: PropTypes.arrayOf(BookedVacationPropType),
        WorklogTracker: PropTypes.arrayOf(
          PropTypes.shape({
            TaskId: PropTypes.number.isRequired,
            TaskName: PropTypes.string.isRequired,
            ProjectId: PropTypes.number.isRequired,
            ProjectName: PropTypes.string,
            OwnerUserId: PropTypes.number,
            OwnerUserName: PropTypes.string,
            EditorUserId: PropTypes.number.isRequired,
            EditorUserName: PropTypes.string,
            Type: PropTypes.string,
            TypeColor: PropTypes.string,
            LastUpdated: PropTypes.string,
            CreationTime: PropTypes.string,
            CreationTimeString: PropTypes.string,
            WeekString: PropTypes.string,
            HoursThisWeek: PropTypes.number,
            WorkingWeekend: PropTypes.bool,
            HoursNextWeek: PropTypes.number,
            Comments: PropTypes.string,
            AllowWorklogEdit: PropTypes.bool.isRequired,
            TableWorklog: PropTypes.arrayOf(TablePropType),
          })
        ).isRequired,
        REProjectLogWidgetDto: PropTypes.shape({
          ActiveProjectLog: PropTypes.arrayOf(REProjectLogPropType),
          InactiveProjectLog: PropTypes.arrayOf(REProjectLogPropType),
        }),
      })
    ),
  }),
  fetchData: PropTypes.func,
};

DashboardRealEstate.defaultProps = {
  realEstateUsers: {
    RETrackersDtos: [],
  },
};

export default DashboardRealEstate;
